/* ═══════════════════════════════════════════════════════════════════════════
   LES CONTRAINTES VOLONTAIRES — ce que la SIMULATION en connaît.

   L'arbre de méta (§6.9) en vend quatre. Elles vont dans le sens inverse de la
   puissance : elles retirent quelque chose au joueur et le paient en crédits.
   Ce module ne contient donc AUCUN chiffre de crédit — il ne connaît que la
   moitié qui change le déroulement d'une partie. Le tarif vit dans
   `src/game/meta.ts`, qui est la couche économique, et la présentation dans
   `src/content/constraints/`.

   ── POURQUOI UN MASQUE DE BITS PLUTÔT QU'UN TABLEAU D'IDENTIFIANTS ──

   Le même argument que pour `PlayerState.secondary` : l'état de run entre dans
   l'empreinte de replay (`Snapshot.ts`), et un entier se hache en un mot
   machine là où un tableau de chaînes ne se hache pas du tout. Un masque évite
   aussi toute allocation à la lecture — `emitterSystem` interroge la contrainte
   de cadence à chaque tick, dans la boucle la plus chaude du jeu.

   ⚠️ Les valeurs des bits sont un FORMAT DE SAUVEGARDE dès qu'une run est
   rejouée : elles ne se réordonnent pas, on en ajoute à la suite.
   ═══════════════════════════════════════════════════════════════════════════ */

export const Constraint = {
  None: 0,
  /** +35 % de cadence de tir ennemie. */
  MareeNoire: 1,
  /** Une seule vie, et aucun gain de vie de la run. */
  CoqueDeVerre: 2,
  /** Les cartes n'affichent plus leur effet chiffré. Aucun effet de moteur. */
  Amnesie: 4,
  /** La boutique de bord reste fermée. Aucun effet de moteur. */
  ZeroAchat: 8,
} as const;

export type ConstraintBit = (typeof Constraint)[keyof typeof Constraint];

/** Toutes les contraintes réunies. Sert de borne de validation à une valeur
 *  venue d'une sauvegarde : un masque hors de ce domaine désigne des bits que
 *  cette version ne sait pas interpréter. */
export const CONSTRAINT_MASK_ALL =
  Constraint.MareeNoire | Constraint.CoqueDeVerre | Constraint.Amnesie | Constraint.ZeroAchat;

/**
 * Facteur appliqué à la cadence de tir ennemie par « Marée noire ».
 *
 * ⚠️ MULTIPLICATIF, et c'est tout l'enjeu du réglage. La cadence ennemie est
 * déjà pilotée par la difficulté (`FIRE_RATE_MUL` : 70 / 100 / 120 %). Une
 * contrainte qui POSERAIT 1,35 au lieu de multiplier par 1,35 ferait passer le
 * Vétéran de 1,20 à 1,35 (+12 %) et la Recrue de 0,70 à 1,35 (+93 %) : elle
 * adoucirait relativement le palier dur tout en payant +45 % de crédits au
 * même tarif. Ce n'est pas un déséquilibre, c'est un bug économique — le
 * chemin le plus rentable deviendrait le plus facile.
 *
 * En multiplicatif, la contrainte vaut exactement +35 % à chaque palier et le
 * Vétéran reste strictement le plus dangereux comme le mieux payé.
 */
export const MAREE_NOIRE_FIRE_RATE = 1.35;

/** Vies imposées par « Coque de verre ». Une, quelles que soient la difficulté
 *  (qui en donne +2 en Recrue) et la branche PV de l'arbre. */
export const COQUE_DE_VERRE_LIVES = 1;

export function hasConstraint(mask: number, bit: number): boolean {
  return (mask & bit) !== 0;
}

/** Nettoie un masque venu de l'extérieur (sauvegarde, paramètre d'URL). Un bit
 *  inconnu vaudrait un comportement inconnu : on le laisse tomber plutôt que de
 *  le propager jusque dans l'empreinte de replay. */
export function sanitizeMask(mask: number): number {
  return (mask | 0) & CONSTRAINT_MASK_ALL;
}

/** Nombre de contraintes actives. Le cumul est plafonné par la méta ; cette
 *  fonction sert à le VÉRIFIER, jamais à l'imposer. */
export function constraintCount(mask: number): number {
  let n = 0;
  let m = mask & CONSTRAINT_MASK_ALL;
  while (m !== 0) {
    n += m & 1;
    m >>>= 1;
  }
  return n;
}

/**
 * Facteur à appliquer PAR-DESSUS la cadence de difficulté.
 *
 * Renvoie 1 quand la contrainte n'est pas prise : l'appelant multiplie donc
 * toujours, sans branche, et la valeur neutre ne peut pas être oubliée.
 */
export function enemyFireRateMul(mask: number): number {
  return hasConstraint(mask, Constraint.MareeNoire) ? MAREE_NOIRE_FIRE_RATE : 1;
}

/**
 * Plafond de vies imposé par les contraintes. `0` = aucun plafond.
 *
 * ⚠️ C'est un PLAFOND continu, pas une valeur de départ. « Coque de verre »
 * promet deux choses — une seule vie, ET aucun gain de vie de la run — et la
 * seconde ne peut pas être tenue par une écriture unique au démarrage : il
 * suffirait qu'un ramassable, une carte ou la boutique en accorde une pour que
 * la contrainte soit contournée sans que personne ne l'ait voulu. Le plafond
 * est donc réappliqué par `playerSystem` à chaque tick.
 */
export function livesCap(mask: number): number {
  return hasConstraint(mask, Constraint.CoqueDeVerre) ? COQUE_DE_VERRE_LIVES : 0;
}

/** La boutique de bord est-elle fermée pour la run ? Lu par la couche
 *  application : la simulation n'a pas de boutique. */
export function shopClosed(mask: number): boolean {
  return hasConstraint(mask, Constraint.ZeroAchat);
}

/**
 * Les cartes doivent-elles masquer leur effet chiffré ?
 *
 * Lu par l'interface. La règle §6.7 impose l'effet chiffré exact ; « Amnésie »
 * la suspend, et l'exception ne tient que parce qu'elle est CHOISIE — la règle
 * protège le joueur qui n'a rien demandé, pas celui qui a payé pour s'en
 * priver. C'est aussi pourquoi cette contrainte ne touche aucun réglage de
 * simulation : ce n'est pas le jeu qui devient plus dur, c'est l'information
 * qui disparaît.
 */
export function cardNumbersHidden(mask: number): boolean {
  return hasConstraint(mask, Constraint.Amnesie);
}
